
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component
export default class RangeSlider extends Vue {
	@Prop({ type: String, default: '' }) private id!: string;
	@Prop({ type: String, default: 'primary' }) private color!: string;
	@Prop({ type: String, default: 'grey lighten-2' }) private trackColor!: string;
	@Prop({ type: String, default: '' }) private firstThumbColor!: string;
	@Prop({ type: String, default: '' }) private secondThumbColor!: string;
	@Prop({ type: String, default: '12px' }) private thumbSize!: string;
	@Prop({ type: String, default: '2px' }) private trackHeight!: string;
	@Prop({ type: [String, Array, Object], default: '' }) private customClass!: string | string[] | object;
	@Prop({ type: Array, required: true }) private value!: number[];
	@Prop({ type: Number, default: 0 }) private min!: number;
	@Prop({ type: Number, default: 100 }) private max!: number;
	@Prop({ type: Number, default: 1 }) private step!: number;
	@Prop({ type: Number, default: 5 }) private tickSize!: number;
	@Prop({ type: Number, default: 0 }) private tickInterval!: number;
	@Prop({ type: Number, default: 1 }) private trackFillOpacity!: number;
	@Prop({ type: [Boolean, String], default: false }) private ticks!: boolean | string;
	@Prop({ type: Boolean, default: false }) private disabled!: boolean;
	@Prop({ type: Boolean, default: false }) private disableFirst!: boolean;
	@Prop({ type: Boolean, default: true }) private showThumbRipple!: boolean;
	@Prop({ type: Boolean, default: true }) private showThumbLabel!: boolean;
	@Watch('tickInterval')
	private onTickIntervalChanged(): void {
		this.updateTickIntervalStyle();
	}

	private tickStyleEl: HTMLStyleElement | null = null;

	/**
	 * Build dynamic CSS classes based on props
	 *
	 * @return {(string | object)[]}
	 */
	private get sliderClasses(): (string | object)[] {
		return [
			this.customClass,
			{
				'no-thumb-ripple': !this.showThumbRipple,
				'no-thumb-label': !this.showThumbLabel,
				'first-thumb-disabled': this.disableFirst,
				'first-thumb-custom': !!this.firstThumbColor
			}
		];
	}

	/**
	 * Build CSS custom properties for dynamic styling
	 *
	 * @return {Record<string, string>}
	 */
	private get sliderStyles(): Record<string, string> {
		const styles: Record<string, string> = {
			'--thumb-size': this.thumbSize,
			'--track-height': this.trackHeight,
			'--track-fill-opacity': String(this.trackFillOpacity)
		};

		if (this.firstThumbColor) {
			styles['--first-thumb-color'] = this.resolveColor(this.firstThumbColor);
		}

		if (this.secondThumbColor) {
			styles['--second-thumb-color'] = this.resolveColor(this.secondThumbColor);
		}

		if (this.tickInterval) {
			styles['--tick-interval'] = String(this.tickInterval);
		}

		return styles;
	}

	private mounted(): void {
		this.updateTickIntervalStyle();
	}

	private beforeDestroy(): void {
		if (this.tickStyleEl) {
			this.tickStyleEl.remove();
		}
	}

	/**
	 * Emit an event, locking the first thumb value when disableFirst is true
	 *
	 * @param {string} event
	 * @param {number[]} newValue
	 * @return {void}
	 */
	private emitWithLock(event: string, newValue: number[]): void {
		if (this.disableFirst) {
			newValue = [this.value[0], newValue[1]];
		}
		this.$emit(event, newValue);
	}

	/**
	 * Resolve a color string through Vuetify's theme, falling back to the raw value
	 *
	 * @param {string} color
	 * @return {string}
	 */
	private resolveColor(color: string): string {
		return this.$vuetify.theme.themes.light[color] as string || color;
	}

	/**
	 * Dynamically inject tick interval CSS since nth-child doesn't support CSS variables.
	 * Uses a style tag scoped to this instance via a data attribute.
	 *
	 * @return {void}
	 */
	private updateTickIntervalStyle(): void {
		if (this.tickInterval <= 0) {
			return;
		}

		if (!this.tickStyleEl) {
			this.tickStyleEl = document.createElement('style');
			document.head.appendChild(this.tickStyleEl);
		}

		const uid = this.id || (this as any)._uid;
		this.$el?.setAttribute('data-slider-uid', String(uid));

		this.tickStyleEl.textContent = `
			[data-slider-uid="${uid}"] .v-slider__tick:nth-child(${this.tickInterval}n+1) {
				display: block !important;
			}
		`;
	}
}
